import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Hub } from "aws-amplify/utils";
import { createContext, useCallback, useEffect, useState } from "react";
import { authenticationService } from "../../services/authentication";
import type { AuthenticatedUser } from "../../services/authentication/types";
import { DistributorUserRole } from "../../services/backend_client";
import type { AuthenticationContextProviderProps, AuthenticationContextValue } from "./types";

export const AuthenticationContext = createContext<AuthenticationContextValue | undefined>(undefined);

export const AuthenticationContextProvider = ({ children }: AuthenticationContextProviderProps) => {
  const [user, setUser] = useState<AuthenticatedUser>();
  // TODO: It would be better to move this fields to myAccountInfo
  const [userId, setUserId] = useState<string>("");
  const [schoolId, setSchoolId] = useState<string>("");
  const [distributorId, setDistributorId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  ///
  const [role, setRole] = useState<string>("");

  const queryClient = useQueryClient();

  const { isLoading: isDeterminingAuthenticationStatus, data: authData } = useQuery<AuthenticatedUser | null>({
    queryKey: ["getAuthenticatedUser"],
    queryFn: async () => {
      try {
        const identity = await authenticationService.getAuthenticatedUser();
        const [attributes, session] = await Promise.all([authenticationService.getAuthenticatedUserAttributes(), authenticationService.getCurrentSession()]);

        const groups = session.tokens?.accessToken?.payload["cognito:groups"];
        const cognitoGroups = Array.isArray(groups) ? groups.join(",") : undefined;

        const authenticatedUser: AuthenticatedUser = {
          userId: identity.userId,
          username: identity.username,
          attributes: {
            email: attributes.email ?? "",
            "custom:UserId": attributes["custom:UserId"] ?? "",
            "custom:Role": attributes["custom:Role"] ?? "",
            "custom:SchoolId": attributes["custom:SchoolId"],
            "custom:DistributorId": attributes["custom:DistributorId"],
            "cognito:groups": cognitoGroups,
          },
        };

        return authenticatedUser;
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "UserUnAuthenticatedException") {
          return null;
        }
        throw error;
      }
    },
    retry: false,
  });

  useEffect(() => {
    const authenticatedUser = authData;
    if (!authenticatedUser) return;

    const attrs = authenticatedUser.attributes;
    setUser(authenticatedUser);
    setUserId(attrs["custom:UserId"]);
    setEmail(attrs.email);

    const userRole = attrs["custom:Role"] ?? "";
    setRole(userRole);

    if (userRole === DistributorUserRole.DistributorAdmin || userRole === DistributorUserRole.Agent) {
      setDistributorId(attrs["custom:DistributorId"] ?? "");
    } else {
      setSchoolId(attrs["custom:SchoolId"] ?? "");
    }
  }, [authData]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signInWithRedirect") {
        queryClient.invalidateQueries({ queryKey: ["getAuthenticatedUser"] });
      }
      if (payload.event === "signInWithRedirect_failure") {
        console.error("Google SSO failed:", payload.data);
      }
    });

    return unsubscribe;
  }, [queryClient]);

  const getJwtToken = useCallback(async () => {
    const session = await authenticationService.getCurrentSession();
    return session.tokens?.idToken?.toString() ?? "";
  }, []);

  const value = {
    isDeterminingAuthenticationStatus,
    user,
    setUser,
    getJwtToken,
    userId,
    setUserId,
    schoolId,
    setSchoolId,
    distributorId,
    setDistributorId,
    role,
    setRole,
    email,
    setEmail,
  };

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};
